// Admin navigation warmup (admin cache audit, follow-up to issue #174).
//
// Two perceived-latency sinks when navigating the backoffice:
//   1. every sidebar target is a lazy chunk → first click pays the JS fetch;
//   2. every page mounts with a cold query cache → first paint is a skeleton.
//
// Mirrors the client app's usePrefetchSecondaryTabs idea: warm the route
// chunk once per session and prefetchQuery the exact key the page mounts
// with. prefetchQuery never throws and honours staleTime, so hovering is
// always safe and cheap — a fresh cache entry skips the network entirely.
import { useEffect } from "react";
import type { QueryClient } from "@/lib/query";
import { normalizePagination } from "@/lib/query/pagination";
import { adminDashboardKeys } from "../dashboard/keys";
import { fetchAdminDashboard, fetchAdminRecentTrades } from "../dashboard/api";
import { adminUsersFirstPageQuerySpec } from "../users/hooks";
import { adminOnlineKeys } from "../users/keys";
import { fetchAdminOnlineSessions } from "../users/api";
import { adminTradeKeys } from "../trades/keys";
import { fetchAdminTrades } from "../trades/api";
import { adminRefundKeys } from "../refunds/keys";
import { fetchRefundRequests, type ListRefundsParams } from "../refunds/api";
import { adminPlanKeys } from "../plans/keys";
import { fetchAdminPlans } from "../plans/api";
import { adminPaymentKeys } from "../payments/keys";
import { fetchPendingPayments } from "../payments/api";
import { adminPayoutKeys } from "../payouts/keys";
import {
  fetchPayoutAssets,
  fetchPayoutHistory,
  fetchPayoutSyncStatus,
} from "../payouts/api";
import { adminAdminsKeys } from "../admins/keys";
import { fetchAdminRoles } from "../admins/api";

export const ADMIN_PREFETCH_STALE_MS = 30_000;
// AdminDashboardPage RECENT_TRADES_LIMIT — keep in sync.
const DASHBOARD_RECENT_TRADES_LIMIT = 10;
// RefundRequestsListPage initial filters (PAGE_LIMIT = 20, no status).
const REFUNDS_FIRST_PAGE: ListRefundsParams = {
  status: null,
  page: 1,
  limit: 20,
};

type ChunkWarmer = () => Promise<unknown>;

export interface AdminRouteWarmupSpec {
  /** Seeds the lazy route chunk so the click skips the JS round-trip. */
  readonly warmChunk: ChunkWarmer;
  /** Prefetches the exact first-mount queries of the target page. */
  readonly prefetchQueries?: (qc: QueryClient) => Promise<void>;
}

export function prefetchAdminDashboardQueries(qc: QueryClient): Promise<void> {
  return Promise.all([
    qc.prefetchQuery({
      queryKey: adminDashboardKeys.snapshot(),
      queryFn: fetchAdminDashboard,
      staleTime: ADMIN_PREFETCH_STALE_MS,
    }),
    qc.prefetchQuery({
      queryKey: adminDashboardKeys.recentTrades(DASHBOARD_RECENT_TRADES_LIMIT),
      queryFn: () =>
        fetchAdminRecentTrades({ limit: DASHBOARD_RECENT_TRADES_LIMIT }),
      staleTime: ADMIN_PREFETCH_STALE_MS,
    }),
  ]).then(() => undefined);
}

function prefetchAdminUsersQueries(qc: QueryClient): Promise<void> {
  const spec = adminUsersFirstPageQuerySpec();
  return qc.prefetchQuery({ ...spec, staleTime: ADMIN_PREFETCH_STALE_MS });
}

function prefetchAdminOnlineSessionsQueries(qc: QueryClient): Promise<void> {
  // OnlineSessionsPage mounts useAdminOnlineSessions({ limit: 100 }).
  const normalized = normalizePagination({ limit: 100 }, { limit: 100 });
  return qc.prefetchQuery({
    queryKey: adminOnlineKeys.list(normalized),
    queryFn: () => fetchAdminOnlineSessions(normalized),
    staleTime: ADMIN_PREFETCH_STALE_MS,
  });
}

function prefetchAdminTradesQueries(qc: QueryClient): Promise<void> {
  // AdminTradesPage mounts useAdminTrades({ limit: 50, offset: 0 }).
  const { limit, offset } = normalizePagination({}, { limit: 50 });
  return qc.prefetchQuery({
    queryKey: adminTradeKeys.list({ limit, offset }),
    queryFn: () => fetchAdminTrades({ limit, offset }),
    staleTime: ADMIN_PREFETCH_STALE_MS,
  });
}

function prefetchAdminRefundsQueries(qc: QueryClient): Promise<void> {
  return qc.prefetchQuery({
    queryKey: adminRefundKeys.list(REFUNDS_FIRST_PAGE),
    queryFn: () => fetchRefundRequests(REFUNDS_FIRST_PAGE),
    staleTime: ADMIN_PREFETCH_STALE_MS,
  });
}

function prefetchAdminPlansQueries(qc: QueryClient): Promise<void> {
  // PlansListPage mounts useAdminPlans() → normalized to limit 50 / offset 0.
  const { limit, offset } = normalizePagination({}, { limit: 50 });
  return qc.prefetchQuery({
    queryKey: adminPlanKeys.list(limit, offset),
    queryFn: () => fetchAdminPlans({ limit, offset }),
    staleTime: ADMIN_PREFETCH_STALE_MS,
  });
}

function prefetchPendingPaymentsQueries(qc: QueryClient): Promise<void> {
  // PendingPaymentsPage mounts usePendingPayments({ limit:20 }).
  const { limit, offset } = normalizePagination({ limit: 20 }, { limit: 50 });
  return qc.prefetchQuery({
    queryKey: adminPaymentKeys.pending(limit, offset, ""),
    queryFn: () => fetchPendingPayments({ limit, offset }),
    staleTime: ADMIN_PREFETCH_STALE_MS,
  });
}

function prefetchAdminPayoutsQueries(qc: QueryClient): Promise<void> {
  return Promise.all([
    qc.prefetchQuery({
      queryKey: adminPayoutKeys.status(),
      queryFn: fetchPayoutSyncStatus,
      staleTime: ADMIN_PREFETCH_STALE_MS,
    }),
    qc.prefetchQuery({
      queryKey: adminPayoutKeys.assets(),
      queryFn: fetchPayoutAssets,
      staleTime: ADMIN_PREFETCH_STALE_MS,
    }),
    qc.prefetchQuery({
      // PayoutsPage mounts usePayoutHistory({ limit: 10, page: 1 }).
      queryKey: adminPayoutKeys.history({ page: 1, limit: 10 }),
      queryFn: () => fetchPayoutHistory(1, 10),
      staleTime: ADMIN_PREFETCH_STALE_MS,
    }),
  ]).then(() => undefined);
}

function prefetchAdminRolesQueries(qc: QueryClient): Promise<void> {
  // AdminsPage mounts useAdminRoles({ limit: 200 }).
  const normalized = normalizePagination({ limit: 200 }, { limit: 50 });
  return qc.prefetchQuery({
    queryKey: adminAdminsKeys.roleList(normalized),
    queryFn: () => fetchAdminRoles(normalized),
    staleTime: ADMIN_PREFETCH_STALE_MS,
  });
}

// Routes without a stable first-mount key (logs read URL params, webhooks
// build composite filters) still get chunk warming — the JS fetch is the
// larger share of their first-click latency anyway.
export const ADMIN_ROUTE_WARMUPS: Readonly<
  Record<string, AdminRouteWarmupSpec>
> = {
  "/admin": {
    warmChunk: () => import("../pages/AdminDashboardPage"),
    prefetchQueries: prefetchAdminDashboardQueries,
  },
  "/admin/users": {
    warmChunk: () => import("../users/pages/UsersListPage"),
    prefetchQueries: prefetchAdminUsersQueries,
  },
  "/admin/online": {
    warmChunk: () => import("../users/pages/OnlineSessionsPage"),
    prefetchQueries: prefetchAdminOnlineSessionsQueries,
  },
  "/admin/trades": {
    warmChunk: () => import("../trades/pages/AdminTradesPage"),
    prefetchQueries: prefetchAdminTradesQueries,
  },
  "/admin/refunds": {
    warmChunk: () => import("../refunds/pages/RefundRequestsListPage"),
    prefetchQueries: prefetchAdminRefundsQueries,
  },
  "/admin/plans": {
    warmChunk: () => import("../plans/pages/PlansListPage"),
    prefetchQueries: prefetchAdminPlansQueries,
  },
  "/admin/payments/pending": {
    warmChunk: () => import("../payments/pages/PendingPaymentsPage"),
    prefetchQueries: prefetchPendingPaymentsQueries,
  },
  "/admin/payouts": {
    warmChunk: () => import("../payouts/pages/PayoutsPage"),
    prefetchQueries: prefetchAdminPayoutsQueries,
  },
  "/admin/admins": {
    warmChunk: () => import("../admins/pages/AdminsPage"),
    prefetchQueries: prefetchAdminRolesQueries,
  },
  "/admin/logs": {
    warmChunk: () => import("../logs/pages/AdminLogsPage"),
  },
  "/admin/matrix-webhooks": {
    warmChunk: () => import("../matrix-webhooks/pages/AdminMatrixWebhooksPage"),
  },
  "/admin/test-mode/history": {
    warmChunk: () => import("../test-mode/pages/HistoryPage"),
  },
  "/admin/whatsapp": {
    warmChunk: () => import("../whatsapp/pages/AdminWhatsappPage"),
  },
  // #229: chunk-only warm (the page reads its first-mount queries from local
  // tab state, so there is no stable prefetch key to seed).
  "/admin/gamification": {
    warmChunk: () => import("../gamification/pages/AdminGamificationPage"),
  },
};

// Chunk warming is once-per-session (the module cache holds the chunk);
// query prefetching re-runs on every hover so staleTime decides freshness.
const warmedChunkHrefs = new Set<string>();

/** Test hook — clears the once-per-session chunk warm bookkeeping. */
export function resetWarmedAdminChunks(): void {
  warmedChunkHrefs.clear();
}

/**
 * Warm one sidebar target. Safe to call on every pointerenter/focus.
 * `warmups` is injectable so tests don't pay the real page-chunk imports.
 *
 * @example onPointerEnter={() => warmAdminRoute(qc, "/admin/users")}
 */
export function warmAdminRoute(
  qc: QueryClient | null,
  href: string,
  warmups: Readonly<Record<string, AdminRouteWarmupSpec>> = ADMIN_ROUTE_WARMUPS,
): void {
  const spec = warmups[href];
  if (!spec) return;
  if (!warmedChunkHrefs.has(href)) {
    warmedChunkHrefs.add(href);
    spec.warmChunk().catch(() => {
      // A failed chunk fetch must stay silent — lazyWithRetry owns the
      // user-visible retry path when the route actually mounts.
      warmedChunkHrefs.delete(href);
    });
  }
  if (qc && spec.prefetchQueries) void spec.prefetchQueries(qc);
}

// Same idle gate as usePrefetchSecondaryTabs (issue #174): wait for an idle
// frame so warmup never competes with the page the operator is looking at.
function scheduleIdleWarmup(callback: () => void): () => void {
  const win = globalThis as typeof globalThis & {
    requestIdleCallback?: (
      cb: () => void,
      opts?: { timeout?: number },
    ) => number;
    cancelIdleCallback?: (id: number) => void;
  };
  if (typeof win.requestIdleCallback === "function") {
    const id = win.requestIdleCallback(callback, { timeout: 2_000 });
    return () => win.cancelIdleCallback?.(id);
  }
  const timeoutId = globalThis.setTimeout(callback, 800);
  return () => globalThis.clearTimeout(timeoutId);
}

/**
 * AdminLayout mount warmup: prefetch the dashboard KPI queries once the
 * main thread is idle, so "Dashboard" is instant from any /admin route.
 */
export function useAdminShellWarmup(qc: QueryClient | null): void {
  useEffect(() => {
    if (qc === null) return undefined;
    return scheduleIdleWarmup(() => {
      void prefetchAdminDashboardQueries(qc);
    });
  }, [qc]);
}
